import { Typography } from '@/components';
import { CaptureButtonIcon } from '@/components/atoms/icons/CaptureButtonIcon';
import { CornerBottomLeftIcon } from '@/components/atoms/icons/CornerBottomLeftIcon';
import { CornerBottomRightIcon } from '@/components/atoms/icons/CornerBottomRightIcon';
import { CornerTopLeftIcon } from '@/components/atoms/icons/CornerTopLeftIcon';
import { CornerTopRightIcon } from '@/components/atoms/icons/CornerTopRightIcon';
import { ZapIcon } from '@/components/atoms/icons/ZapIcon';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { asyncStorage } from '@/core/storage';
import { Ionicons } from '@expo/vector-icons';
import * as FileSystem from 'expo-file-system/legacy';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Dimensions, Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { autoCropDocument, getImageDimensions } from '../utils/imageProcessing';
// VisionCamera only works on native platforms, not web
const isNative = Platform.OS !== 'web';
let Camera: any;
let useCameraDevice: any;
let useCameraPermission: any;

if (isNative) {
    try {
        const visionCamera = require('react-native-vision-camera');
        Camera = visionCamera.Camera;
        useCameraDevice = visionCamera.useCameraDevice;
        useCameraPermission = visionCamera.useCameraPermission;
    } catch (error) {
        // VisionCamera not available
    }
}

type DocumentType = 'emiratesIdFront' | 'emiratesIdBack' | 'passport';

const SCREEN_WIDTH = 393; // Figma design width
const SCREEN_HEIGHT = 852; // Figma design height

export function DocumentScannerScreen() {
    useEffect(() => {
        if (Platform.OS !== 'web' && Camera) {
            trackCatalogEvent({
                screen: DT_SCREEN.DOCUMENT_SCANNER,
                actionName: 'document_scanner_viewed',
                eventType: 'view',
            });
        }
    }, []);

    // Document scanner is not available on web
    if (Platform.OS === 'web') {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="headline" color="primary">
                    Document scanner is not available on web
                </Typography>
            </View>
        );
    }

    // Ensure VisionCamera is available
    if (!Camera || !useCameraDevice || !useCameraPermission) {
        return (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="headline" color="primary">
                    Camera not available
                </Typography>
            </View>
        );
    }

    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const params = useLocalSearchParams();
    const documentType = (params.documentType as DocumentType) || 'emiratesIdFront';
    // Preserve preview mode params if they exist (for editing from preview)
    const previewMode = params.previewMode as string | undefined;
    const hideConfirm = params.hideConfirm as string | undefined;
    const previewDocumentType = params.previewDocumentType as string | undefined;

    const camera = useRef<any>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [flash, setFlash] = useState<'on' | 'off'>('off');
    const [screenDimensions, setScreenDimensions] = useState({ width: SCREEN_WIDTH, height: SCREEN_HEIGHT });

    // Get camera device
    const device = useCameraDevice('back');
    const { hasPermission, requestPermission } = useCameraPermission();

    // Get screen dimensions on mount and update on orientation change
    useEffect(() => {
        const updateDimensions = () => {
            const { width, height } = Dimensions.get('window');
            setScreenDimensions({ width, height });
            logger.info('DocumentScannerScreen', 'Screen dimensions updated', { width, height });
        };

        // Get initial dimensions
        updateDimensions();

        // Listen for dimension changes (orientation changes)
        const subscription = Dimensions.addEventListener('change', updateDimensions);

        return () => {
            subscription?.remove();
        };
    }, []);

    // Request permission on mount
    useEffect(() => {
        if (!hasPermission) {
            requestPermission();
        }
    }, [hasPermission, requestPermission]);

    const handleCapture = useCallback(async () => {
        if (!camera.current || !device || isCapturing) {
            logger.warn('DocumentScannerScreen', 'Cannot capture - camera not ready', {
                hasCamera: !!camera.current,
                hasDevice: !!device,
                isCapturing,
            });
            return;
        }

        try {
            setIsCapturing(true);
            logger.info('DocumentScannerScreen', 'Capturing photo', { documentType });

            // Ensure camera is ready before taking photo
            if (!camera.current) {
                throw new Error('Camera ref is null');
            }

            // Take photo - capture as-is without orientation corrections
            const photo = await camera.current.takePhoto({
                flash: flash === 'on' ? 'on' : 'off',
                enableShutterSound: false,
            });

            logger.info('DocumentScannerScreen', 'Photo captured', { path: photo.path });
            trackCatalogEvent({
                screen: DT_SCREEN.DOCUMENT_SCANNER,
                actionName: 'document_capture_success',
                eventType: 'action',
            });

            // Vision Camera returns a file path, convert to URI
            const photoUri = photo.path.startsWith('file://') ? photo.path : `file://${photo.path}`;

            // Get image dimensions
            const { width, height } = await getImageDimensions(photoUri);

            // Auto-crop based on frame position
            const croppedUri = await autoCropDocument(
                photoUri,
                width,
                height,
                screenDimensions.width,
                screenDimensions.height
            );

            logger.info('DocumentScannerScreen', 'Image cropped', { uri: croppedUri });

            // Use the cropped image URI directly (ImageManipulator already saves it to cache)
            // ImageManipulator returns a file:// URI, use it directly
            let finalUri = croppedUri;

            // Verify the cropped file exists
            try {
                const croppedFileInfo = await FileSystem.getInfoAsync(croppedUri);
                if (!croppedFileInfo.exists) {
                    logger.warn('DocumentScannerScreen', 'Cropped file does not exist, using original photo', { croppedUri });
                    // Fallback to original photo - ensure it has file:// prefix
                    finalUri = photo.path.startsWith('file://') ? photo.path : `file://${photo.path}`;
                }
            } catch (error) {
                logger.warn('DocumentScannerScreen', 'Failed to verify cropped file, using original photo', error);
                // Fallback to original photo
                finalUri = photo.path.startsWith('file://') ? photo.path : `file://${photo.path}`;
            }

            // Save to AsyncStorage
            try {
                const stored = await asyncStorage.getItem('document.images');
                const documents = stored ? JSON.parse(stored) : {};
                // Store the URI as-is (with file:// prefix) - document viewer expects this format
                documents[documentType] = finalUri;
                await asyncStorage.setItem('document.images', JSON.stringify(documents));
                logger.info('DocumentScannerScreen', `Saved document to storage: ${documentType}`, { uri: finalUri });
            } catch (error) {
                logger.error('DocumentScannerScreen', 'Failed to save document to storage', error);
                // Still navigate even if storage fails
            }

            // Small delay to ensure storage write completes
            await new Promise(resolve => setTimeout(resolve, 100));

            trackCatalogEvent({
                screen: DT_SCREEN.DOCUMENT_SCANNER,
                actionName: 'document_scanner_confirm',
                eventType: 'action',
            });
            // Navigate back to previous screen
            router.back();
            
            // Update params on the document-viewer screen after navigation
            // Use a small delay to ensure navigation completes first
            setTimeout(() => {
                router.setParams({
                    croppedImage: finalUri,
                    documentType: documentType,
                    // Preserve preview mode params if they exist
                    ...(previewMode && { previewMode }),
                    ...(hideConfirm && { hideConfirm }),
                    ...(previewDocumentType && { previewDocumentType })
                } as any);
            }, 100);
        } catch (error) {
            logger.error('DocumentScannerScreen', 'Failed to capture photo', error);
            trackCatalogEvent({
                screen: DT_SCREEN.DOCUMENT_SCANNER,
                actionName: 'document_capture_error',
                eventType: 'error',
            });
            setIsCapturing(false);
        }
    }, [camera, device, flash, documentType, screenDimensions, isCapturing]);

    const toggleFlash = useCallback(() => {
        setFlash((prev) => (prev === 'on' ? 'off' : 'on'));
    }, []);

    const handleBack = useCallback(() => {
        router.back();
    }, []);

    // Get title based on document type
    const getTitle = () => {
        if (documentType === 'emiratesIdFront') {
            return t('documentCapture.titleEmiratesIdFront');
        } else if (documentType === 'emiratesIdBack') {
            return t('documentCapture.titleEmiratesIdBack');
        } else {
            return t('documentCapture.titlePassport');
        }
    };

    // Get first instruction based on document type
    const getFirstInstruction = () => {
        if (documentType === 'passport') {
            return t('documentCapture.instructionUseOriginalPassport');
        } else {
            return t('documentCapture.instructionUseOriginalEmiratesId');
        }
    };

    if (!hasPermission) {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                <View style={styles.permissionContainer}>
                    <Typography variant="headline" color="primary" style={styles.centerText}>
                        {t('documentCapture.cameraPermissionRequired')}
                    </Typography>
                    <Typography variant="body" color="secondary" style={[styles.centerText, styles.permissionText]}>
                        {t('documentCapture.cameraPermissionMessage')}
                    </Typography>
                    <Pressable style={styles.permissionButton} onPress={requestPermission}>
                        <Typography variant="headline" color="primary">
                            {t('documentCapture.grantPermission')}
                        </Typography>
                    </Pressable>
                </View>
            </View>
        );
    }

    if (!device) {
        return (
            <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
                <View style={styles.permissionContainer}>
                    <Typography variant="headline" color="primary" style={styles.centerText}>
                        {t('documentCapture.cameraNotAvailable')}
                    </Typography>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {/* Camera Preview */}
            <Camera
                ref={camera}
                style={StyleSheet.absoluteFill}
                device={device}
                isActive={true}
                photo={true}
                torch={flash === 'on' ? 'on' : 'off'}
            />

            {/* Dark Overlay */}
            <View style={styles.overlay} pointerEvents="none" />

            {/* Header - Back Button */}
            <View style={[styles.header, { paddingTop: insets.top + 20 }]} pointerEvents="box-none">
                <Pressable
                    onPress={handleBack}
                    style={[
                        styles.backButton,
                        { alignItems: isRTL ? 'flex-end' : 'flex-start' }
                    ]}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                    <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                        <Ionicons name="chevron-back" size={24} color="#FFFFFF" />
                    </View>
                </Pressable>
            </View>

            {/* Instructions Section */}
            <View style={[styles.instructionsContainer, { top: insets.top + 85 }]}>
                <Typography variant="title1" color="white" style={styles.instructionsTitle}>
                    {getTitle()}
                </Typography>
                <View style={styles.instructionsList}>
                    <View style={[
                        styles.instructionItem,
                        {
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            paddingLeft: isRTL ? 0 : 48,
                            paddingRight: isRTL ? 48 : 0,
                        }
                    ]}>
                        <View style={styles.bulletPoint} />
                        <Typography variant="body" color="white" style={styles.instructionText}>
                            {getFirstInstruction()}
                        </Typography>
                    </View>
                    <View style={[
                        styles.instructionItem,
                        {
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            paddingLeft: isRTL ? 0 : 48,
                            paddingRight: isRTL ? 48 : 0,
                        }
                    ]}>
                        <View style={styles.bulletPoint} />
                        <Typography variant="body" color="white" style={styles.instructionText}>
                            {t('documentCapture.instructionGoodLighting')}
                        </Typography>
                    </View>
                    <View style={[
                        styles.instructionItem,
                        {
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                            paddingLeft: isRTL ? 0 : 48,
                            paddingRight: isRTL ? 48 : 0,
                        }
                    ]}>
                        <View style={styles.bulletPoint} />
                        <Typography variant="body" color="white" style={styles.instructionText}>
                            {t('documentCapture.instructionClearDetails')}
                        </Typography>
                    </View>
                </View>
            </View>

            {/* Document Frame with Corner Icons */}
            <View style={styles.documentFrame}>
                {/* Top Left Corner */}
                <View style={[
                    styles.cornerTopLeft,
                    isRTL && { left: 'auto', right: -2 }
                ]}>
                    {isRTL ? (
                        <CornerTopRightIcon size={71} />
                    ) : (
                        <CornerTopLeftIcon size={71} />
                    )}
                </View>
                {/* Top Right Corner */}
                <View style={[
                    styles.cornerTopRight,
                    isRTL && { right: 'auto', left: -2 }
                ]}>
                    {isRTL ? (
                        <CornerTopLeftIcon size={71} />
                    ) : (
                        <CornerTopRightIcon size={71} />
                    )}
                </View>
                {/* Bottom Left Corner */}
                <View style={[
                    styles.cornerBottomLeft,
                    isRTL && { left: 'auto', right: -2 }
                ]}>
                    {isRTL ? (
                        <CornerBottomRightIcon size={71} />
                    ) : (
                        <CornerBottomLeftIcon size={71} />
                    )}
                </View>
                {/* Bottom Right Corner */}
                <View style={[
                    styles.cornerBottomRight,
                    isRTL && { right: 'auto', left: -2 }
                ]}>
                    {isRTL ? (
                        <CornerBottomLeftIcon size={71} />
                    ) : (
                        <CornerBottomRightIcon size={71} />
                    )}
                </View>
            </View>

            {/* Bottom Controls */}
            <View style={[styles.bottomControls, { paddingBottom: insets.bottom + 20 }]}>
                <View style={styles.controlsContainer}>
                    <Pressable
                        style={[styles.captureButton, isCapturing && styles.captureButtonDisabled]}
                        onPress={handleCapture}
                        disabled={isCapturing}
                    >
                        {isCapturing ? (
                            <ActivityIndicator color="#FFFFFF" />
                        ) : (
                            <CaptureButtonIcon size={66} />
                        )}
                    </Pressable>
                    <Pressable
                        style={[
                            styles.flashButton,
                            isRTL
                                ? { right: '75%', left: 'auto', marginRight: -16, marginLeft: 0 }
                                : { left: '75%', right: 'auto', marginLeft: -16, marginRight: 0 }
                        ]}
                        onPress={toggleFlash}
                    >
                        <ZapIcon size={32} color={flash === 'on' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'} />
                    </Pressable>
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#000',
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1,
    },
    header: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        paddingHorizontal: 16,
        paddingBottom: 20,
        zIndex: 100,
        elevation: 100, // Android elevation
    },
    backButton: {
        width: 40,
        height: 40,
        justifyContent: 'center',
        alignItems: 'flex-start',
        zIndex: 101,
        elevation: 101, // Android elevation
    },
    instructionsContainer: {
        position: 'absolute',
        left: 24,
        right: 24,
        zIndex: 10,
    },
    instructionsTitle: {
        textAlign: 'center',
        marginBottom: 24,
        fontSize: 22,
        lineHeight: 28,
    },
    instructionsList: {
        gap: 8,
    },
    instructionItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    bulletPoint: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: '#F5F5F5',
    },
    instructionText: {
        flex: 1,
        fontSize: 16,
    },
    documentFrame: {
        position: 'absolute',
        top: 304,
        left: 24,
        width: 345,
        height: 244,
        zIndex: 5,
    },
    cornerTopLeft: {
        position: 'absolute',
        top: -2, // Slight offset to align with frame edge
        left: -2,
    },
    cornerTopRight: {
        position: 'absolute',
        top: -2,
        right: -2,
    },
    cornerBottomLeft: {
        position: 'absolute',
        bottom: -2,
        left: -2,
    },
    cornerBottomRight: {
        position: 'absolute',
        bottom: -2,
        right: -2,
    },
    bottomControls: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
    },
    controlsContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        position: 'relative',
    },
    captureButton: {
        width: 66,
        height: 66,
        alignItems: 'center',
        justifyContent: 'center',
    },
    captureButtonDisabled: {
        opacity: 0.5,
    },
    flashButton: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        // Position at midpoint between capture button edge and screen edge
        // Capture button is centered (50%), edge is at 50% + 33px
        // Screen edge is at 100% (or 0% for RTL)
        // Midpoint: (50% + 33px + 100%) / 2 = 75% + 16.5px
        // Flash button center should be at this midpoint
        // RTL/LTR positioning handled dynamically in component
    },
    permissionContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
    },
    permissionText: {
        marginTop: 16,
        marginBottom: 24,
    },
    permissionButton: {
        paddingVertical: 12,
        paddingHorizontal: 24,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
    },
    centerText: {
        textAlign: 'center',
    },
});

export default DocumentScannerScreen;
